import { ReactElement, createElement } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { CircularGauge } from "../atoms/CircularGauge";
import { Co21HistoryTable } from "../atoms/Co21HistoryTable";
import {
  Co21AnalysisResult,
  Co21DetailedAnalysisResult,
  Co21PastDataItem,
} from "../../engines/co2_1Engine";

type HistoryRecord = {
  date: string;
  score: number;
  evaluation: "A" | "B" | "C";
  threeMonthScore?: number;
  currentScore?: number;
};

function RenderMainResult({
  content,
}: Readonly<{
  content: ReactElement;
}>): ReactElement {
  return (
    <Flex flexDirection="column" px={4} pt={0} pb={4} width="100%">
      {content}
    </Flex>
  );
}

function RenderMainContent({
  averageScore,
  currentScore,
  message,
}: Readonly<{
  averageScore: number;
  currentScore: number;
  message: string;
}>): ReactElement {
  const { t } = useTranslation();

  return (
    <Flex flexDirection="column" alignItems="center">
      {/* Title */}
      <Box textAlign="center" mb={4}>
        <Text fontSize="2xl" fontWeight="bold" color="#555555">
          {t("PJ.Co21Result.Title")}
        </Text>
      </Box>

      {/* Circular Gauge */}
      <Box display="flex" justifyContent="center" width="100%">
        <Box
          position="relative"
          width="300px"
          height="300px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularGauge score={averageScore} maxScore={100} size={300} />
          <Box
            position="absolute"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            <Text
              fontSize="6xl"
              fontWeight="light"
              color="#333333"
              fontFamily="Jost"
              lineHeight="1"
            >
              {averageScore.toFixed(1)}
            </Text>
            <Text fontSize="xs" color="#888888" mt={1} mb={4}>
              {t("PJ.Co21Result.ThreeMonthAverage")}
            </Text>
            <Text
              fontSize="2xl"
              fontWeight="light"
              color="#333333"
              fontFamily="Jost"
            >
              {currentScore}
            </Text>
            <Text fontSize="xs" color="#888888">
              {t("PJ.Co21Result.ThisTime")}
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Message */}
      <Box textAlign="center" mt={2}>
        <Text
          fontSize="xl"
          color="#555555"
          fontWeight="medium"
          whiteSpace="pre-line"
          lineHeight="1.4"
        >
          {message}
        </Text>
      </Box>
    </Flex>
  );
}

export function ResultCo21(
  props: Readonly<{
    title: string;
    averageScore: number;
    currentScore: number;
    message: string;
    description: string;
    co21Result: Co21AnalysisResult;
    onHomeClick?: () => void;
  }>,
): ReactElement {
  const detailed = props.co21Result
    .detailed_analysis_result as Co21DetailedAnalysisResult | null;
  const pastData: Co21PastDataItem[] =
    detailed?.ninety_day_average_past_data || [];

  // 最新のデータを作成（現在の結果から）
  const currentData: Co21PastDataItem = {
    id: -1,
    recording_id: "",
    analyzed_at: props.co21Result.analyzed_at || new Date().toISOString(),
    analysis_result: props.co21Result.analysis_result,
  };

  // 最新データと過去データを結合
  const allData: Co21PastDataItem[] = [currentData, ...pastData];

  // HistoryRecord形式に変換
  const historyRecords: HistoryRecord[] = allData.map((item) => {
    const analysisResult = item.analysis_result;
    const ninetyDayAvg = analysisResult.ninety_day_average;
    const avgScore = ninetyDayAvg.cognitive_score;
    const avgDecision = ninetyDayAvg.decision;
    const currentScore = analysisResult.cognitive_score;

    return {
      date: item.analyzed_at,
      score: avgScore,
      evaluation: avgDecision as "A" | "B" | "C",
      threeMonthScore: avgScore,
      currentScore: currentScore,
    };
  });

  return (
    <Flex flexDirection="column" mb="20px">
      <Flex flexDirection="column" mb="10px">
        <RenderMainResult
          content={createElement(RenderMainContent, {
            averageScore: props.averageScore,
            currentScore: props.currentScore,
            message: props.message,
          })}
        />
      </Flex>

      {/* 3-Month History Section */}
      <Box mt={0} display="flex" flexDirection="column" alignItems="center">
        <Box width="90%" maxW="600px">
          <Co21HistoryTable records={historyRecords} />
        </Box>
      </Box>

      {/* Description */}
      <Box mt={6} display="flex" flexDirection="column" alignItems="center">
        <Box width="90%" maxW="600px">
          <Text fontSize="sm" color="#666666" lineHeight="1.8">
            {props.description}
          </Text>
        </Box>
      </Box>
    </Flex>
  );
}
